import i18n from '@/utils/i18n';
import { Button, Dialog, Icons } from '@usepepper/sprout-kit-ui';
import { CatalogItemRow } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/catalog-item-row';
import { CatalogSearchSkeleton } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/catalog-search-skeleton';
import { createStyles } from '@/contexts/theme-context';
import { EditOrderGuideActionType } from '@/experiences/sales-portal/domain/order-guides/blocs/reducers/use-edit-order-guide-reducer';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { FlatList, View } from 'react-native';
import { groupByItem } from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/group-by-item';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import {
  SearchInput,
  SearchInputRef,
} from '@/experiences/sales-portal/components/search-input';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  useEditOrderGuideActions,
  usePartitionSelection,
} from '@/experiences/sales-portal/domain/order-guides/blocs/contexts/edit-order-guide-context';
import { useSearchCatalogForOrderGuideLoader } from '@/experiences/sales-portal/domain/order-guides/blocs/loaders/use-search-catalog-for-order-guide-loader';

const SEARCH_DEBOUNCE_MS = 200;
const DIALOG_HEIGHT = 720;
const DIALOG_WIDTH = 480;
const DIALOG_HEADER_FOOTER_PADDING = 24;

interface AddItemsCatalogBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export const AddItemsCatalogBrowser: React.FC<AddItemsCatalogBrowserProps> = ({
  isOpen,
  onClose,
  restaurantUUID,
  supplierUUID,
}) => {
  const [styles, theme] = useStyles();
  const { selectedPartition } = usePartitionSelection();
  const dispatch = useEditOrderGuideActions();
  const { cancelSearch, error, loading, results, search } =
    useSearchCatalogForOrderGuideLoader({ restaurantUUID, supplierUUID });

  const [term, setTerm] = useState('');
  const [selectedPacks, setSelectedPacks] = useState<
    Map<string, OrderGuideItem>
  >(new Map());
  const [expandedItemUUIDs, setExpandedItemUUIDs] = useState<Set<string>>(
    new Set(),
  );
  const searchRef = useRef<SearchInputRef>(null);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    void search(value);
  }, SEARCH_DEBOUNCE_MS);

  const groupedResults = useMemo(
    () => groupByItem(results, (item) => item.itemUUID),
    [results],
  );

  const existingPackUUIDs = useMemo(
    () =>
      new Set(
        (selectedPartition?.itemGroups ?? [])
          .flatMap((group) => group.items)
          .map((item) => item.variantPackUUID),
      ),
    [selectedPartition],
  );

  const handleSearchChange = useCallback(
    (text: string) => {
      setTerm(text);
      if (!text.trim()) {
        debouncedSearch.cancel();
        void search('');
        return;
      }
      debouncedSearch(text);
    },
    [debouncedSearch, search],
  );

  const togglePack = useCallback(
    (pack: OrderGuideItem) => {
      if (existingPackUUIDs.has(pack.variantPackUUID)) {
        return;
      }
      setSelectedPacks((previous) => {
        const next = new Map(previous);
        if (next.has(pack.variantPackUUID)) {
          next.delete(pack.variantPackUUID);
        } else {
          next.set(pack.variantPackUUID, pack);
        }
        return next;
      });
    },
    [existingPackUUIDs],
  );

  // Toggle a whole item: select every selectable (not already-added) pack, or
  // clear them all if they're already selected.
  const toggleItem = useCallback(
    (packs: OrderGuideItem[]) => {
      const selectable = packs.filter(
        (pack) => !existingPackUUIDs.has(pack.variantPackUUID),
      );
      if (selectable.length === 0) {
        return;
      }
      setSelectedPacks((previous) => {
        const next = new Map(previous);
        const allSelected = selectable.every((pack) =>
          next.has(pack.variantPackUUID),
        );
        selectable.forEach((pack) => {
          if (allSelected) {
            next.delete(pack.variantPackUUID);
          } else {
            next.set(pack.variantPackUUID, pack);
          }
        });
        return next;
      });
    },
    [existingPackUUIDs],
  );

  const toggleExpand = useCallback((itemUUID: string) => {
    setExpandedItemUUIDs((previous) => {
      const next = new Set(previous);
      if (next.has(itemUUID)) {
        next.delete(itemUUID);
      } else {
        next.add(itemUUID);
      }
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    cancelSearch();
    debouncedSearch.cancel();
    setTerm('');
    setSelectedPacks(new Map());
    setExpandedItemUUIDs(new Set());
    onClose();
  }, [cancelSearch, debouncedSearch, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  // Steer the Dialog's open autofocus to the search input instead of the
  // first focusable (the close button).
  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    searchRef.current?.focus();
  }, []);

  const handleAdd = useCallback(() => {
    const items = [...selectedPacks.values()];
    if (items.length === 0 || !selectedPartition) {
      return;
    }
    dispatch({
      items,
      partitionUUID: selectedPartition.partitionUUID,
      type: EditOrderGuideActionType.ADD_ITEM,
    });
    handleClose();
  }, [dispatch, handleClose, selectedPacks, selectedPartition]);

  const selectedCount = selectedPacks.size;
  const selectedPackUUIDs = useMemo(
    () => new Set(selectedPacks.keys()),
    [selectedPacks],
  );
  const isSearching = loading || debouncedSearch.isPending();
  const groupName =
    selectedPartition?.name ??
    i18n.t('sales_portal.order_guides_list.groups.default');

  const renderBody = () => {
    if (!term.trim()) {
      return (
        <EmptyState
          description={i18n.t(
            'sales_portal.order_guides_list.add_items.empty_no_term_description',
          )}
          iconElement={
            <Icons.Search color={theme.sk.color.icon.tertiary} size="$32" />
          }
          title={i18n.t(
            'sales_portal.order_guides_list.add_items.empty_no_term_title',
          )}
        />
      );
    }
    if (isSearching) {
      return <CatalogSearchSkeleton />;
    }
    if (error) {
      return (
        <ErrorState
          description={i18n.t(
            'sales_portal.order_guides_list.add_items.error_description',
          )}
          onPress={() => void search(term)}
          title={i18n.t('sales_portal.order_guides_list.add_items.error_title')}
        />
      );
    }
    if (groupedResults.length === 0) {
      return (
        <EmptyState
          description={i18n.t(
            'sales_portal.order_guides_list.add_items.empty_no_results_description',
          )}
          iconElement={
            <Icons.Search color={theme.sk.color.icon.tertiary} size="$32" />
          }
          title={i18n.t(
            'sales_portal.order_guides_list.add_items.empty_no_results_title',
          )}
        />
      );
    }
    return (
      <FlatList
        data={groupedResults}
        keyExtractor={(group) => group.itemUUID}
        renderItem={({ item: group }) => (
          <CatalogItemRow
            addedPackUUIDs={existingPackUUIDs}
            expandable={group.items.length > 1}
            expanded={expandedItemUUIDs.has(group.itemUUID)}
            onToggleExpand={() => toggleExpand(group.itemUUID)}
            onToggleItem={() => toggleItem(group.items)}
            onTogglePack={togglePack}
            packs={group.items}
            selectedPackUUIDs={selectedPackUUIDs}
          />
        )}
      />
    );
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <Dialog.Content
        height={DIALOG_HEIGHT}
        onOpenAutoFocus={handleOpenAutoFocus}
        paddingHorizontal={0}
        width={DIALOG_WIDTH}
      >
        <Dialog.Header paddingHorizontal={DIALOG_HEADER_FOOTER_PADDING}>
          <Dialog.Title>
            {i18n.t('sales_portal.order_guides_list.add_items.title', {
              group: groupName,
            })}
          </Dialog.Title>
        </Dialog.Header>
        <View style={styles.body}>
          <View style={styles.searchbar}>
            <SearchInput
              onEscapePress={handleClose}
              onSearchChange={handleSearchChange}
              placeholderText={i18n.t(
                'sales_portal.order_guides_list.add_items.search_placeholder',
              )}
              ref={searchRef}
              searchText={term}
            />
          </View>
          <View style={styles.list}>{renderBody()}</View>
        </View>
        <Dialog.Footer
          fade
          paddingHorizontal={DIALOG_HEADER_FOOTER_PADDING}
          paddingTop={0}
        >
          <Button
            accessibilityLabel={i18n.t('cancel')}
            onPress={handleClose}
            size="$md"
            variant="secondary"
          >
            {i18n.t('cancel')}
          </Button>
          <Button
            accessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.add_items.add_button_label',
            )}
            disabled={selectedCount === 0}
            onPress={handleAdd}
            size="$md"
          >
            {selectedCount > 0
              ? i18n.t(
                  'sales_portal.order_guides_list.add_items.add_button_with_count',
                  { count: selectedCount },
                )
              : i18n.t(
                  'sales_portal.order_guides_list.add_items.add_button_label',
                )}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

const useStyles = createStyles((theme) => ({
  body: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  list: {
    flex: 1,
  },
  searchbar: {
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
}));
